import { forwardRef, InputHTMLAttributes, LegacyRef, useState, ChangeEvent, ReactNode, useRef } from 'react';
import { cn } from '@/utils/classNames.ts';
import { InputProps } from 'react-daisyui';
import { ChevronDownIcon } from '@heroicons/react/24/solid';
import SelectInputDropdown from '@/components/functional/SelectInput/SelectInputDropdown.tsx';

type Option<T> = {
  value: T;
  label: string;
};

type SelectInputProps<T> = { onChange?: (value: T) => void; options: Option<T>[] } & Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'size' | 'color' | 'onChange' | 'value'
> &
  Pick<InputProps, 'size' | 'color' | 'bordered'> & {
    label?: ReactNode;
    error?: string;
    wrapperClassName?: string;
    labelClassName?: string;
  } & { value: T; searchable?: boolean; searchablePlaceholder?: string; showAsText?: boolean };

const SelectInput = forwardRef(
  <T,>(
    {
      label,
      error,
      wrapperClassName,
      labelClassName,
      size = 'md',
      color,
      bordered = true,
      value,
      onChange,
      options,
      className,
      searchable = false,
      searchablePlaceholder = 'Search...',
      showAsText = false,
      ...args
    }: SelectInputProps<T>,
    ref: LegacyRef<HTMLInputElement>
  ) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    const buttonRef = useRef<HTMLButtonElement>(null);

    const handleSelect = (optionValue: T) => {
      if (onChange) {
        onChange(optionValue);
      }

      setSearchQuery('');
      setIsOpen(false);
    };

    const handleSearch = (e: ChangeEvent<HTMLInputElement>) => {
      setSearchQuery(e.target.value);
    };

    const handleOpenDropdown = () => {
      if (isOpen) return;
      setIsOpen(true);
    };

    const availableOptions = !searchable
      ? options
      : options.filter((option) => option.label.toLowerCase().includes(searchQuery.toLowerCase()));

    return (
      <div className={cn('form-control max-w-full w-full', wrapperClassName)}>
        {label ? (
          <label className="label">
            <span className="label-text">{label}</span>
          </label>
        ) : null}
        <button
          type="button"
          ref={buttonRef}
          onClick={handleOpenDropdown}
          className={cn('input flex items-center justify-between gap-2 cursor-pointer max-w-full', labelClassName, {
            'input-ghost': color === 'ghost',
            'input-primary': color === 'primary',
            'input-secondary': color === 'secondary',
            'input-accent': color === 'accent',
            'input-info': color === 'info',
            'input-success': color === 'success',
            'input-warning': color === 'warning',
            'input-error': error || color === 'error',
            'input-xs': size === 'xs',
            'input-sm': size === 'sm',
            'input-md': size === 'md' || size,
            'input-lg': size === 'lg',
            'input-bordered': bordered,
          })}
        >
          {showAsText ? (
            <div className={className}>{options.find((option) => option.value === value)?.label ?? ''}</div>
          ) : null}
          <input
            tabIndex={0}
            className={cn(
              'text-base grow cursor-pointer overflow-auto max-w-[calc(100%-1rem)]',
              {
                hidden: showAsText,
              },
              className
            )}
            ref={ref}
            readOnly
            value={options.find((option) => option.value === value)?.label ?? ''}
            {...args}
          />
          {isOpen && (
            <SelectInputDropdown
              options={availableOptions}
              value={value}
              searchable={searchable}
              searchablePlaceholder={searchablePlaceholder}
              searchQuery={searchQuery}
              onSearchChange={handleSearch}
              onSelect={handleSelect}
              onClose={() => setIsOpen(false)}
              buttonRef={buttonRef}
              name={args.name}
            />
          )}
          <ChevronDownIcon className="w-4 shrink-0" />
        </button>
        {error ? (
          <label className="label">
            <span className="label-text-alt text-error">{error}</span>
          </label>
        ) : null}
      </div>
    );
  }
) as <T>(props: SelectInputProps<T> & { ref?: LegacyRef<HTMLInputElement> }) => JSX.Element;

export default SelectInput;
